import React from 'react';
import { useContentBlocks, ContentBlock } from '../lib/hooks/useContentBlocks';
import RichText from './RichText';

interface TextImageSectionProps {
  blockKey: 'textImage' | 'imageText';
  imageSide: 'left' | 'right';
}

// A content section with text on one side and an image on the other.
// Renders nothing until it has some content, and falls back to a single
// centered text column when no image is set.
const TextImageSection: React.FC<TextImageSectionProps> = ({ blockKey, imageSide }) => {
  const { blocks, loading } = useContentBlocks();
  const block: ContentBlock = blocks[blockKey];

  if (loading) return null;
  if (!block || (!block.title && !block.text && !block.imageUrl)) return null;

  const TextCol = (
    <div className={imageSide === 'left' ? 'md:order-2' : ''}>
      {block.subtitle && (
        <p className="text-secondary font-semibold uppercase tracking-wide mb-2">
          {block.subtitle}
        </p>
      )}
      {block.title && (
        <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">{block.title}</h2>
      )}
      <RichText value={block.text} className="text-neutral-600 leading-relaxed" />
      {block.buttonText && block.buttonLink && (
        <a href={block.buttonLink} className="btn-primary inline-block mt-6">
          {block.buttonText}
        </a>
      )}
    </div>
  );

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container-custom">
        {block.imageUrl ? (
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {TextCol}
            <div className={imageSide === 'left' ? 'md:order-1' : ''}>
              <img
                src={block.imageUrl}
                alt={block.title || ''}
                className="w-full h-auto max-h-[28rem] object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto text-center">{TextCol}</div>
        )}
      </div>
    </section>
  );
};

export default TextImageSection;
